/**
 * #1167 S4: a big folder move runs as a `file-move` job and `filesApi.moveFile` resolves only once it is done —
 * minutes, for a big tree. While it runs the person sees a progress toast ("Moving "X"…"), opened when the server
 * answers 202 (`onQueued`) and dismissed when the move settles; the caller's own result toast (moved / split /
 * refused) follows as before. An inline move opens nothing. A toaster without `progress` (a test double) shows none.
 */
export interface MoveProgressToaster {
  progress?: (message: string, total: number | null) => { id: string }
  removeToast?: (id: string) => void
}

export type MoveHooks = { onQueued: (info: { jobId: string }) => void }

export async function withMoveProgress<R>(
  toast: MoveProgressToaster, message: string, run: (hooks: MoveHooks) => Promise<R>,
  /** #1316 review round 2: a pinned batch's step opens no progress once the batch's session ended (`batch.ended`). */
  ended?: () => boolean,
): Promise<R> {
  let id: string | null = null
  try {
    return await run({ onQueued: () => { if (id === null && toast.progress && !ended?.()) id = toast.progress(message, 0).id } })
  } finally {
    if (id !== null) toast.removeToast?.(id)
  }
}

/**
 * A move re-sent from a dialog (the chain-escape "Move anyway" / "Share the target" re-call): the dialog stays busy
 * only while the server answers. Once the move is queued (a big folder, minutes) the dialog closes (`close`) and the
 * progress toast carries it; an inline move leaves closing to the caller, after its result.
 */
export function withDialogMoveProgress<R>(
  toast: MoveProgressToaster, message: string, close: () => void, run: (hooks: MoveHooks) => Promise<R>,
): Promise<R> {
  return withMoveProgress(toast, message, (hooks) => run({ onQueued: (info) => { hooks.onQueued(info); close() } }))
}
